import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiAward, FiTrendingUp, FiUsers, FiHome } = FiIcons;

const About = () => {
  const stats = [
    { number: '500+', label: 'Properties Managed', icon: FiHome },
    { number: '98%', label: 'Client Satisfaction', icon: FiAward },
    { number: '15+', label: 'Years Experience', icon: FiTrendingUp },
    { number: '24/7', label: 'Support Available', icon: FiUsers }
  ];

  const values = [
    {
      title: 'Professionalism',
      description: 'We maintain the highest standards of professionalism in all our interactions and services.',
      icon: FiAward
    },
    {
      title: 'Transparency',
      description: 'Clear communication and honest reporting ensure you always know the status of your investment.',
      icon: FiTarget
    },
    {
      title: 'Customer Satisfaction',
      description: 'Your success is our success. We are committed to exceeding expectations in every aspect.',
      icon: FiUsers
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                About DGM Property Management
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  DGM Property Management is Calgary's trusted partner for comprehensive property management services. We specialize in managing day-to-day operations proactively to maintain properties at optimal standards while ensuring clear communication with property owners and residents.
                </p>
                <p>
                  Our team focuses on maximizing your investment returns through strategic tenant placement, efficient maintenance coordination, and transparent financial management. We serve the Calgary area with additional services in Airdrie, providing localized expertise and personalized attention.
                </p>
                <p>
                  With our commitment to professionalism, transparency, and customer satisfaction, we handle everything from tenant acquisition to property enhancement, allowing you to enjoy the benefits of real estate investment without the day-to-day hassles.
                </p>
              </div>
            </div>

            {/* Values */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-gray-900">Our Core Values</h3>
              <div className="space-y-4">
                {values.map((value, index) => (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={value.icon} className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{value.title}</h4>
                      <p className="text-gray-600 text-sm">{value.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Image and Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Image */}
            <div className="relative overflow-hidden rounded-2xl shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                alt="Calgary skyline and modern buildings - DGM Property Management service area"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <h4 className="text-xl font-semibold">Calgary & Airdrie</h4>
                <p className="text-gray-200">Serving Alberta's Premier Markets</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 text-center shadow-lg"
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <SafeIcon icon={stat.icon} className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-blue-600 mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;